import { Box, Center, Flex, Image, Text } from "@chakra-ui/react";
import { YoutubeIframePlayer } from "../news/components/YoutubeIframePlayer";
import { FeedVideoItem } from "./types";

interface FeedShortVideoCardProps {
  item: FeedVideoItem;
  isActive: boolean;
}

/**
 * Feature card for short-form vertical videos (YouTube Shorts). Full-screen
 * 9:16 with the YouTube iframe mounted only while active — neighbors render
 * the still thumbnail to avoid spinning up multiple players offscreen.
 */
export function FeedShortVideoCard({
  item,
  isActive,
}: FeedShortVideoCardProps) {
  const thumb =
    item.thumbnail_url ??
    `https://i.ytimg.com/vi/${item.youtube_id}/hqdefault.jpg`;
  // mute=1 is required for browser autoplay policy — without it Chrome /
  // Safari silently block the autoplay and leave a static thumbnail with no
  // controls visible (looks like the video just doesn't play).
  const embedSrc =
    `https://www.youtube.com/embed/${item.youtube_id}` +
    `?autoplay=1&mute=1&loop=1&playlist=${item.youtube_id}` +
    `&modestbranding=1&rel=0&playsinline=1&controls=1`;

  return (
    <Box
      w="100%"
      h="100%"
      position="relative"
      overflow="hidden"
      bg="black"
    >
      {isActive ? (
        <YoutubeIframePlayer w="100%" h="100%">
          <iframe
            src={embedSrc}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              objectFit: "cover",
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            title={item.title}
          />
        </YoutubeIframePlayer>
      ) : (
        <>
          <Image
            src={thumb}
            alt={item.title}
            position="absolute"
            inset={0}
            w="100%"
            h="100%"
            objectFit="cover"
            opacity={0.85}
          />
          <Center position="absolute" inset={0} pointerEvents="none">
            <Box
              w="72px"
              h="72px"
              borderRadius="full"
              bg="rgba(255,255,255,0.9)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow="0 8px 24px rgba(0,0,0,0.4)"
            >
              <Box
                as="span"
                display="inline-block"
                w="0"
                h="0"
                ml="6px"
                borderTop="14px solid transparent"
                borderBottom="14px solid transparent"
                borderLeft="22px solid black"
              />
            </Box>
          </Center>
        </>
      )}
      <Flex
        position="absolute"
        left={0}
        right={0}
        bottom={0}
        direction="column"
        gap={1}
        px={5}
        py={6}
        color="white"
        bgGradient="linear(180deg, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.85) 100%)"
        pointerEvents="none"
      >
        <Text
          fontSize="xs"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="wide"
          opacity={0.85}
        >
          Short
        </Text>
        <Text fontSize="lg" fontWeight="bold" lineHeight="1.25" noOfLines={3}>
          {item.title}
        </Text>
      </Flex>
    </Box>
  );
}
